import { useState, useEffect, useMemo, useCallback, memo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  TrendingUp, 
  TrendingDown, 
  Activity, 
  Brain, 
  Zap, 
  Heart,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Calendar,
  BarChart3
} from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { insightsService } from "@/services/insightsService";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";

// Type definition for pattern analysis - may need to be adjusted based on backend response
interface UserPatternAnalysis {
  energyPatterns: {
    peakTimes: string[];
    lowTimes: string[];
    weeklyTrends: { [key: string]: number };
    aiInsight?: string;
  };
  moodPatterns: {
    triggers: { positive: string[]; negative: string[] };
    trends: { improving: boolean; stable: boolean; declining: boolean };
    averageByDay: { [key: string]: number };
    aiInsight?: string;
  };
  productivityPatterns: {
    bestConditions: string[];
    blockers: string[];
    successFactors: string[];
    aiInsight?: string;
  };
  predictions: Array<{
    prediction: string;
    timeframe: string;
    confidence: number;
    evidence: string[];
    recommendation: string;
  }>;
  overallScore: {
    wellbeing: number;
    trend: 'improving' | 'stable' | 'declining';
    riskFactors: string[];
    strengths: string[];
    aiInsight?: string;
  };
}

const PatternAnalysisDashboard = () => {
  const { user } = useUser();
  const [analysis, setAnalysis] = useState<UserPatternAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'quarter'>('month');
  const [lastLoadedUser, setLastLoadedUser] = useState<string | null>(null);
  const [lastLoadedTimeRange, setLastLoadedTimeRange] = useState<'week' | 'month' | 'quarter'>('month');

  const loadAnalysis = useCallback(async (range?: 'week' | 'month' | 'quarter') => {
    if (!user) return;

    const selectedRange = range || timeRange;
    
    // Don't reload if we already have data for the same user and timeframe
    if (analysis && lastLoadedUser === user.id && lastLoadedTimeRange === selectedRange && !range) {
      return;
    }

    setIsLoading(true);
    try {
      const days = selectedRange === 'week' ? 7 : selectedRange === 'month' ? 30 : 90;
      
      // MIGRATED TO BACKEND: Use getUserPatternAnalysis for dashboard (returns UserPatternAnalysis format matching web_old)
      // Backend endpoint: GET /insights/user-pattern-analysis
      // MIGRATED FROM web_old/src/services/patternPredictionService.ts
      // No transformation needed - already UserPatternAnalysis format!
      const patternAnalysis = await insightsService.getUserPatternAnalysis(days).catch(() => null);
      
      if (patternAnalysis) {
        // No transformation needed - backend already returns UserPatternAnalysis format (matching web_old)
        setAnalysis(patternAnalysis);
        setLastLoadedUser(user.id);
        setLastLoadedTimeRange(selectedRange);
      } else {
        // Fallback: use old API if getUserPatternAnalysis fails
        const response = await insightsService.getPersonalizedInsights().catch(() => null);
        if (response) {
      const backendPatterns = response?.patterns || response;
      
          // Minimal fallback mapping (just for compatibility)
      const weeklyTrends: { [key: string]: number } = {
        Monday: backendPatterns?.energyPatterns?.averageEnergy || 50,
        Tuesday: backendPatterns?.energyPatterns?.averageEnergy || 50,
        Wednesday: backendPatterns?.energyPatterns?.averageEnergy || 50,
        Thursday: backendPatterns?.energyPatterns?.averageEnergy || 50,
        Friday: backendPatterns?.energyPatterns?.averageEnergy || 50,
        Saturday: backendPatterns?.energyPatterns?.averageEnergy || 50,
        Sunday: backendPatterns?.energyPatterns?.averageEnergy || 50,
      };

          const fallbackAnalysis: UserPatternAnalysis = {
        energyPatterns: {
          peakTimes: backendPatterns?.energyPatterns?.peakTimes || [],
          lowTimes: backendPatterns?.energyPatterns?.lowTimes || [],
          weeklyTrends,
          aiInsight: backendPatterns?.predictions?.nextWeekOutlook || '',
        },
        moodPatterns: {
          triggers: { positive: [], negative: [] },
          trends: {
            improving: backendPatterns?.energyPatterns?.trend === 'improving',
            stable: backendPatterns?.energyPatterns?.trend === 'stable',
            declining: backendPatterns?.energyPatterns?.trend === 'declining',
          },
          averageByDay: {
            Monday: 3, Tuesday: 3, Wednesday: 3, Thursday: 3, Friday: 3, Saturday: 3, Sunday: 3
          },
          aiInsight: '',
        },
        productivityPatterns: {
          bestConditions: backendPatterns?.behavioralPatterns?.successFactors || [],
          blockers: backendPatterns?.behavioralPatterns?.commonBlockers || [],
          successFactors: backendPatterns?.behavioralPatterns?.successFactors || [],
          aiInsight: '',
        },
        predictions: backendPatterns?.predictions?.suggestedAdjustments?.map((adj: string, i: number) => ({
          prediction: adj,
          timeframe: 'Next 7 days',
          confidence: 0.7,
          evidence: ['Based on your recent patterns'],
          recommendation: backendPatterns?.burnoutRisk?.recommendations?.[i] || 'Continue building on your positive habits',
        })) || [],
        overallScore: {
          wellbeing: 100 - (backendPatterns?.burnoutRisk?.score || 0),
          trend: backendPatterns?.energyPatterns?.trend || 'stable',
          riskFactors: backendPatterns?.burnoutRisk?.factors || [],
          strengths: backendPatterns?.behavioralPatterns?.successFactors || [],
          aiInsight: backendPatterns?.burnoutRisk?.recommendations?.[0] || '',
        },
      };
      
          setAnalysis(fallbackAnalysis);
      setLastLoadedUser(user.id);
      setLastLoadedTimeRange(selectedRange);
        }
      }
    } catch (error) {
      console.error('Error loading pattern analysis:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user, timeRange, analysis, lastLoadedUser, lastLoadedTimeRange]);

  useEffect(() => {
    if (!user?.id) return;
    
    // Track that user has visited the patterns/insights page for onboarding checklist
    localStorage.setItem(`hasVisited_patterns_${user.id}`, 'true');
    
    // Load analysis on mount or when user changes
    loadAnalysis();
  }, [user?.id, loadAnalysis]);

  const handleTimeRangeChange = useCallback((value: string) => {
    const newRange = value as 'week' | 'month' | 'quarter';
    setTimeRange(newRange);
    // Clear current analysis to force reload with new timeframe
    setAnalysis(null);
    loadAnalysis(newRange);
  }, [loadAnalysis]);

  // Prepare chart data - memoized to prevent recalculation on every render
  // NOTE: All hooks must be called before any early returns (Rules of Hooks)
  const weeklyData = useMemo(() => {
    if (!analysis) return [];
    return Object.entries(analysis.energyPatterns?.weeklyTrends || {}).map(([day, energy]) => ({
      day: day.slice(0, 3),
      energy: Math.round(energy as number),
      mood: analysis.moodPatterns?.averageByDay?.[day] ? Math.round(analysis.moodPatterns.averageByDay[day] * 20) : 0
    }));
  }, [analysis]);

  // Early return AFTER all hooks
  if (!user) return null;

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving': return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'declining': return <TrendingDown className="h-4 w-4 text-red-500" />;
      default: return <Activity className="h-4 w-4 text-blue-500" />;
    }
  };

  const getWellbeingColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Enhanced Header */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-card dark:to-muted/50 rounded-lg p-6 border border-purple-200 dark:border-border">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3 text-foreground">
              <div className="p-2 bg-purple-100 dark:bg-muted rounded-lg">
                <Brain className="h-6 w-6 text-purple-600 dark:text-primary" />
              </div>
              Future Pattern Intelligence
            </h1>
            <p className="text-muted-foreground mt-2 text-lg">
              Predict future trends, understand deep patterns, and optimize your personal performance
            </p>
          </div>
          <Button onClick={() => { setAnalysis(null); loadAnalysis(); }} disabled={isLoading} size="lg">
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            {isLoading ? 'Analyzing...' : 'Refresh Analysis'}
          </Button>
        </div>

        {/* Quick stats and filters */}
        <div className="flex flex-wrap items-center gap-4">
          <Select value={timeRange} onValueChange={handleTimeRangeChange}>
            <SelectTrigger className="w-[180px]">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Select time range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Last 7 days</SelectItem>
              <SelectItem value="month">Last 30 days</SelectItem>
              <SelectItem value="quarter">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-3">
            <Badge variant="secondary" className="bg-white/80 dark:bg-card text-purple-700 dark:text-primary">
              <BarChart3 className="w-3 h-3 mr-1" />
              {analysis ? `${analysis.predictions.length} insights` : 'Loading...'}
            </Badge>
            <Badge variant="secondary" className="bg-white/80 dark:bg-card text-blue-700 dark:text-primary">
              <Calendar className="w-3 h-3 mr-1" />
              {timeRange === 'week' ? 'Last 7 days' : timeRange === 'month' ? 'Last 30 days' : 'Last 90 days'}
            </Badge>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse space-y-4">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-8 bg-muted rounded"></div>
                  <div className="h-4 bg-muted rounded w-1/2"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : analysis ? (
        <>
          {/* Overall Score */}
          <Card className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-card dark:to-muted/50 border-purple-200 dark:border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <Heart className="h-5 w-5 text-red-500" />
                Overall Wellbeing Score
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-2xl font-bold ${analysis.overallScore.wellbeing ? getWellbeingColor(analysis.overallScore.wellbeing) : 'text-muted-foreground'}`}>
                      {analysis.overallScore.wellbeing ? Math.round(analysis.overallScore.wellbeing) + '%' : 'No data yet'}
                    </span>
                    {getTrendIcon(analysis.overallScore.trend)}
                    <span className="text-sm text-muted-foreground capitalize">
                      {analysis.overallScore.trend}
                    </span>
                  </div>
                  <Progress value={analysis.overallScore.wellbeing || 0} className="h-2" />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div>
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-1">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Your Strengths
                  </h4>
                  <div className="space-y-1">
                    {analysis.overallScore.strengths.map((strength, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {strength}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                {analysis.overallScore.riskFactors.length > 0 && (
                  <div>
                    <h4 className="font-medium text-sm mb-2 flex items-center gap-1">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      Areas to Watch
                    </h4>
                    <div className="space-y-1">
                      {analysis.overallScore.riskFactors.map((risk, index) => (
                        <Badge key={index} variant="destructive" className="text-xs">
                          {risk}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Tabs defaultValue="predictions" className="space-y-4">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="predictions">🔮 Future Predictions</TabsTrigger>
              <TabsTrigger value="patterns">🧠 Deep Patterns</TabsTrigger>
              <TabsTrigger value="productivity">⚡ Optimization</TabsTrigger>
              <TabsTrigger value="trends">📈 Trajectory</TabsTrigger>
            </TabsList>

            {/* Future Predictions Tab */}
            <TabsContent value="predictions" className="space-y-6">
              {/* Future Timeline */}
              <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-card dark:to-muted/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Brain className="h-5 w-5 text-indigo-600" />
                    Your Future Performance Timeline
                  </CardTitle>
                  <CardDescription>
                    AI-predicted patterns for the next 30 days based on your historical data
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4">
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="p-4 bg-white/60 dark:bg-card/60 rounded-lg">
                        <h4 className="font-semibold text-sm mb-2 text-green-700">🚀 Next Week (High Confidence)</h4>
                        <p className="text-sm text-muted-foreground">
                          {analysis.energyPatterns.peakTimes.length > 0 
                            ? `Peak energy expected on ${analysis.energyPatterns.peakTimes[0]}. Schedule important work then.`
                            : 'Analyzing your energy patterns to predict optimal performance times.'}
                        </p>
                      </div>
                      <div className="p-4 bg-white/60 dark:bg-card/60 rounded-lg">
                        <h4 className="font-semibold text-sm mb-2 text-amber-700">⚡ Next 2 Weeks (Medium Confidence)</h4>
                        <p className="text-sm text-muted-foreground">
                          {analysis.overallScore.trend === 'declining' 
                            ? 'Current declining trend suggests planning lighter workload and recovery activities.'
                            : analysis.overallScore.trend === 'improving'
                            ? 'Momentum building - you could handle increased challenges in week 2.'
                            : 'Stable patterns suggest maintaining current routine with minor optimizations.'}
                        </p>
                      </div>
                      <div className="p-4 bg-white/60 dark:bg-card/60 rounded-lg">
                        <h4 className="font-semibold text-sm mb-2 text-blue-700">🔮 Next Month (Predictive)</h4>
                        <p className="text-sm text-muted-foreground">
                          {analysis.overallScore.wellbeing > 75
                            ? 'Strong momentum building - excellent month ahead if patterns hold.'
                            : analysis.overallScore.wellbeing > 60
                            ? 'Steady progress expected with consistent effort and pattern optimization.'
                            : 'Recovery period recommended - focus on building sustainable foundations.'}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Detailed Predictions */}
              <div className="grid gap-4">
                {(analysis.predictions || []).map((prediction, index) => (
                  <Card key={index} className="border-l-4 border-l-indigo-500 hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-start">
                        <div className="space-y-1">
                          <CardTitle className="text-base flex items-center gap-2">
                            🔮 {prediction.prediction}
                          </CardTitle>
                          <CardDescription className="text-sm font-medium">
                            📅 {prediction.timeframe}
                          </CardDescription>
                        </div>
                        <div className="text-right">
                          <Badge variant={prediction.confidence > 0.8 ? 'default' : prediction.confidence > 0.6 ? 'secondary' : 'outline'}>
                            {Math.round(prediction.confidence * 100)}% confidence
                          </Badge>
                          <p className="text-xs text-muted-foreground mt-1">
                            {prediction.confidence > 0.8 ? 'High certainty' : prediction.confidence > 0.6 ? 'Likely' : 'Possible'}
                          </p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-medium text-sm mb-2 flex items-center gap-1">
                            📊 Pattern Evidence:
                          </h4>
                          <ul className="text-sm text-muted-foreground space-y-1">
                            {(prediction.evidence || []).map((evidence, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full mt-2 flex-shrink-0"></div>
                                {evidence}
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-card dark:to-muted/50 p-3 rounded-lg">
                          <h4 className="font-medium text-sm mb-2 text-blue-900 dark:text-foreground flex items-center gap-1">
                            🎯 Optimization Strategy:
                          </h4>
                          <p className="text-sm text-blue-800 dark:text-muted-foreground">{prediction.recommendation}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Patterns Tab */}
            <TabsContent value="patterns" className="space-y-4">
              <div className="grid gap-4">
                {/* Weekly Pattern Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="h-5 w-5 text-blue-500" />
                      Weekly Energy & Mood Patterns
                    </CardTitle>
                    <CardDescription>
                      Your average energy and mood levels throughout the week
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={weeklyData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                          <XAxis dataKey="day" />
                          <YAxis />
                          <Tooltip 
                            contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #e2e8f0' }}
                            labelStyle={{ color: '#1f2937' }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="energy" 
                            stroke="#f59e0b" 
                            strokeWidth={2}
                            dot={{ fill: '#f59e0b', r: 4 }}
                            name="Energy"
                          />
                          <Line 
                            type="monotone" 
                            dataKey="mood" 
                            stroke="#3b82f6" 
                            strokeWidth={2}
                            dot={{ fill: '#3b82f6', r: 4 }}
                            name="Mood"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <div className="grid md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Zap className="h-5 w-5 text-amber-500" />
                        Energy Insights
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <h4 className="font-medium text-sm mb-2">🔥 Peak Performance Times:</h4>
                        <div className="space-y-1">
                          {analysis.energyPatterns.peakTimes.length > 0 ? (
                            analysis.energyPatterns.peakTimes.map((time, index) => (
                              <Badge key={index} variant="secondary" className="mr-2 mb-1">
                                {time}
                              </Badge>
                            ))
                          ) : (
                            <p className="text-sm text-muted-foreground">More data needed to identify patterns</p>
                          )}
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="font-medium text-sm mb-2">😴 Energy Dips:</h4>
                        <div className="space-y-1">
                          {analysis.energyPatterns.lowTimes.length > 0 ? (
                            analysis.energyPatterns.lowTimes.map((time, index) => (
                              <Badge key={index} variant="outline" className="mr-2 mb-1">
                                {time}
                              </Badge>
                            ))
                          ) : (
                            <p className="text-sm text-muted-foreground">No significant dips detected</p>
                          )}
                        </div>
                      </div>

                      {analysis.energyPatterns.aiInsight && (
                        <div className="pt-2 border-t">
                          <p className="text-sm text-muted-foreground">
                            🧠 {analysis.energyPatterns.aiInsight}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Heart className="h-5 w-5 text-red-500" />
                        Mood Patterns
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <h4 className="font-medium text-sm mb-2">😊 Mood Boosters:</h4>
                        <div className="space-y-1">
                          {analysis.moodPatterns.triggers.positive.map((trigger, index) => (
                            <Badge key={index} variant="secondary" className="mr-2 mb-1 bg-green-100 text-green-800">
                              {trigger}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="font-medium text-sm mb-2">😔 Mood Challenges:</h4>
                        <div className="space-y-1">
                          {analysis.moodPatterns.triggers.negative.map((trigger, index) => (
                            <Badge key={index} variant="outline" className="mr-2 mb-1 border-red-200 text-red-700">
                              {trigger}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div className="pt-2 border-t space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Mood Trend:</span>
                          {analysis.moodPatterns.trends.improving && (
                            <Badge className="bg-green-100 text-green-800">Improving</Badge>
                          )}
                          {analysis.moodPatterns.trends.stable && (
                            <Badge className="bg-blue-100 text-blue-800">Stable</Badge>
                          )}
                          {analysis.moodPatterns.trends.declining && (
                            <Badge className="bg-red-100 text-red-800">Needs Attention</Badge>
                          )}
                        </div>
                        {analysis.moodPatterns.aiInsight && (
                          <p className="text-sm text-muted-foreground">
                            🧠 {analysis.moodPatterns.aiInsight}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>

            {/* Productivity Tab */}
            <TabsContent value="productivity" className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">🎯 Best Conditions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {analysis.productivityPatterns.bestConditions.map((condition, index) => (
                        <div key={index} className="flex items-start gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          {condition}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">🚧 Common Blockers</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {analysis.productivityPatterns.blockers.map((blocker, index) => (
                        <div key={index} className="flex items-start gap-2 text-sm">
                          <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                          {blocker}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">⭐ Success Factors</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {analysis.productivityPatterns.successFactors.map((factor, index) => (
                        <div key={index} className="flex items-start gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                          {factor}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* AI Insight for Productivity */}
              {analysis.productivityPatterns.aiInsight && (
                <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-card dark:to-muted/50">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Brain className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-blue-900 dark:text-foreground">
                        {analysis.productivityPatterns.aiInsight}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Trends Tab */}
            <TabsContent value="trends" className="space-y-4">
              <div className="grid gap-4">
                {/* Overall Trend Summary */}
                <Card className="border-l-4 border-l-blue-500">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <TrendingUp className="h-5 w-5" />
                        Overall Trajectory
                      </span>
                      <Badge variant={analysis.overallScore.trend === 'improving' ? 'default' : analysis.overallScore.trend === 'declining' ? 'destructive' : 'secondary'}>
                        {analysis.overallScore.trend.charAt(0).toUpperCase() + analysis.overallScore.trend.slice(1)}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-primary">
                          {Math.round(analysis.overallScore.wellbeing)}%
                        </div>
                        <p className="text-sm text-muted-foreground">Current Wellbeing Score</p>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-bold">
                          {analysis.overallScore.trend === 'improving' ? '+' : analysis.overallScore.trend === 'declining' ? '-' : '~'}
                          {Math.abs(Math.round((analysis.overallScore.wellbeing - 70) / 70 * 100))}%
                        </div>
                        <p className="text-sm text-muted-foreground">Change from Baseline</p>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-bold text-green-600">
                          {analysis.predictions.length}
                        </div>
                        <p className="text-sm text-muted-foreground">Actionable Insights</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Comparative Bar Chart */}
                {weeklyData.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Weekly Comparison</CardTitle>
                      <CardDescription>
                        Energy and mood patterns throughout your week
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={weeklyData}>
                            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                            <XAxis dataKey="day" />
                            <YAxis />
                            <Tooltip 
                              contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #e2e8f0' }}
                              labelStyle={{ color: '#1f2937' }}
                            />
                            <Bar dataKey="energy" fill="#f59e0b" name="Energy %" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="mood" fill="#3b82f6" name="Mood Score" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Key Metrics Grid */}
                <div className="grid md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Positive Trends</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {analysis.overallScore.strengths.map((strength, index) => (
                          <div key={index} className="flex items-start gap-2">
                            <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                            <span className="text-sm">{strength}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Areas for Growth</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {analysis.overallScore.riskFactors.map((risk, index) => (
                          <div key={index} className="flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5" />
                            <span className="text-sm">{risk}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* AI Trend Analysis */}
                {analysis.overallScore.aiInsight && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Brain className="h-5 w-5 text-purple-500" />
                        AI Trajectory Intelligence
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {analysis.overallScore.aiInsight}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </>
      ) : (
        <Card>
          <CardContent className="p-6 text-center">
            <Brain className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-medium mb-2">No pattern data available</h3>
            <p className="text-muted-foreground">
              Complete a few more check-ins to see your personalized pattern analysis
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default memo(PatternAnalysisDashboard);